import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { buyersAPI } from '@/lib/api-client'
import { formatDocument } from '@/lib/utils'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { BuyerFormDialog } from './BuyerFormDialog'
import { BuyerMemberDialog } from './BuyerMemberDialog'
import { SaleFormDialog } from './SaleFormDialog'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  UserPlus,
  Users,
  ShoppingBag,
  Loader2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Plus,
} from 'lucide-react'

const SALE_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  SIGNED: 'Assinado',
  PAID: 'Pago',
  CANCELLED: 'Cancelado',
}

const SALE_STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  SIGNED: 'bg-blue-100 text-blue-700',
  PAID: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString('pt-BR')
}

export function BuyerDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const canEdit = usePermission('buyers:edit')
  const canDelete = usePermission('buyers:delete')

  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showSaleDialog, setShowSaleDialog] = useState(false)
  const [editingSale, setEditingSale] = useState<any>(null)
  const [showMemberDialog, setShowMemberDialog] = useState(false)
  const [editingMember, setEditingMember] = useState<any>(null)
  const [deletingSaleId, setDeletingSaleId] = useState<string | null>(null)

  const { data: buyer, isLoading } = useQuery({
    queryKey: ['buyer', id],
    queryFn: () => buyersAPI.getById(id!),
    enabled: !!id,
  })

  const { data: financialData } = useQuery({
    queryKey: ['buyer', id, 'financial-summary'],
    queryFn: () => buyersAPI.getFinancialSummary(id!),
    enabled: !!id,
  })

  const deleteMutation = useMutation({
    mutationFn: () => buyersAPI.delete(id!),
    onSuccess: (data: any) => {
      toast.success(data.message)
      queryClient.invalidateQueries({ queryKey: ['buyers'] })
      navigate('/buyers')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const deleteSaleMutation = useMutation({
    mutationFn: (saleId: string) => buyersAPI.deleteSale(id!, saleId),
    onSuccess: (data: any) => {
      toast.success(data.message)
      queryClient.invalidateQueries({ queryKey: ['buyer', id] })
      queryClient.invalidateQueries({ queryKey: ['buyer', id, 'financial-summary'] })
      setDeletingSaleId(null)
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: (memberId: string) => buyersAPI.removeMember(id!, memberId),
    onSuccess: (data: any) => {
      toast.success(data.message)
      queryClient.invalidateQueries({ queryKey: ['buyer', id] })
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!buyer) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <h2 className="text-xl font-medium text-neutral-900">
          Comprador nao encontrado
        </h2>
        <Button className="mt-4" onClick={() => navigate('/buyers')}>
          Voltar
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/buyers')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <UserPlus className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold text-neutral-900">
                {buyer.name}
              </h1>
              <Badge variant={buyer.isActive ? 'default' : 'secondary'}>
                {buyer.isActive ? 'Ativo' : 'Inativo'}
              </Badge>
            </div>
            <p className="mt-1 text-neutral-500">{formatDocument(buyer.document)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {canEdit && (
            <Button variant="outline" onClick={() => setShowEditDialog(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Editar
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outline"
              className="text-red-600 hover:text-red-700"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Excluir
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="info">
        <div className="overflow-x-auto">
          <TabsList className="w-max min-w-full sm:w-auto">
            <TabsTrigger value="info">Informacoes</TabsTrigger>
            <TabsTrigger value="financial">Financeiro</TabsTrigger>
            <TabsTrigger value="sales">
              Vendas ({buyer._count?.sales || 0})
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Info Tab */}
        <TabsContent value="info" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Contact Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Dados Pessoais</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-neutral-500 w-24">Documento</span>
                  <span className="text-sm text-neutral-900">{formatDocument(buyer.document)}</span>
                </div>
                {buyer.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-neutral-400" />
                    <span className="text-sm text-neutral-900">{buyer.email}</span>
                  </div>
                )}
                {buyer.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-neutral-400" />
                    <span className="text-sm text-neutral-900">{buyer.phone}</span>
                  </div>
                )}
                {buyer.address && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-neutral-400" />
                    <span className="text-sm text-neutral-900">{buyer.address}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contract */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Contrato</CardTitle>
              </CardHeader>
              <CardContent>
                {buyer.contractUrl ? (() => {
                  const isEnvelopeId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(buyer.contractUrl.trim())
                  const href = isEnvelopeId
                    ? `https://apps.docusign.com/send/documents/details/${buyer.contractUrl.trim()}`
                    : buyer.contractUrl
                  return (
                    <div className="space-y-1">
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-4 w-4" />
                        <span className="text-sm">
                          {isEnvelopeId ? 'Abrir no DocuSign' : 'Abrir contrato'}
                        </span>
                      </a>
                      {isEnvelopeId && (
                        <p className="text-xs text-neutral-400 font-mono">
                          Envelope: {buyer.contractUrl}
                        </p>
                      )}
                    </div>
                  )
                })() : (
                  <p className="text-sm text-neutral-500">
                    Nenhum contrato vinculado.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Co-buyers */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-neutral-500" />
                <CardTitle className="text-base">
                  Co-compradores ({buyer.members?.length || 0})
                </CardTitle>
              </div>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setEditingMember(null); setShowMemberDialog(true) }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Adicionar
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {buyer.members?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>CPF</TableHead>
                      <TableHead>E-mail</TableHead>
                      <TableHead>Telefone</TableHead>
                      {canEdit && <TableHead className="w-20" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {buyer.members.map((member: any) => (
                      <TableRow key={member.id}>
                        <TableCell className="font-medium">{member.name}</TableCell>
                        <TableCell>{formatDocument(member.document)}</TableCell>
                        <TableCell>{member.email || '-'}</TableCell>
                        <TableCell>{member.phone || '-'}</TableCell>
                        {canEdit && (
                          <TableCell className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => { setEditingMember(member); setShowMemberDialog(true) }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700"
                              onClick={() => {
                                if (confirm(`Remover ${member.name} dos co-compradores?`)) {
                                  removeMemberMutation.mutate(member.id)
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-neutral-500 text-center py-4">
                  Nenhum co-comprador adicionado. Transações de co-compradores serão conciliadas automaticamente quando cadastrados aqui.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-neutral-500">
                  Total Devido
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-amber-600" />
                  <span className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(financialData?.totalDevido || 0)}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-neutral-500">
                  Total Pago
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  <span className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(financialData?.totalPago || 0)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {(financialData?.totalPermutas || 0) > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-neutral-500">
                    Total Permutas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <ShoppingBag className="h-5 w-5 text-amber-500" />
                    <span className="text-2xl font-bold text-neutral-900">
                      {formatCurrency(financialData?.totalPermutas || 0)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-neutral-500">
                  Saldo Devedor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-red-600" />
                  <span className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(financialData?.saldoDevedor || 0)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Progress bar */}
          {(financialData?.totalDevido || 0) > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-neutral-500">
                  Progresso de Pagamento
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="h-3 w-full rounded-full bg-neutral-200">
                      <div
                        className="h-3 rounded-full bg-green-500 transition-all"
                        style={{ width: `${Math.min(financialData?.percentualPago || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-neutral-700 w-12 text-right">
                    {financialData?.percentualPago || 0}%
                  </span>
                </div>
                <p className="mt-2 text-xs text-neutral-500">
                  {formatCurrency((financialData?.totalPago || 0) + (financialData?.totalPermutas || 0))} de {formatCurrency(financialData?.totalDevido || 0)}
                  {(financialData?.totalPermutas || 0) > 0 && (
                    <span className="ml-1 text-amber-600">
                      (incl. {formatCurrency(financialData?.totalPermutas || 0)} em permutas)
                    </span>
                  )}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Recent transactions */}
          {financialData?.transacoesRecentes?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Transacoes Recentes</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Descricao</TableHead>
                      <TableHead>Projeto</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {financialData.transacoesRecentes.map((tx: any) => (
                      <TableRow key={tx.id}>
                        <TableCell className="text-sm">
                          {formatDate(tx.date)}
                        </TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">
                          {tx.description}
                        </TableCell>
                        <TableCell className="text-sm">
                          {tx.project?.name || '-'}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          <span className={tx.type === 'INCOME' ? 'text-green-600' : 'text-red-600'}>
                            {tx.type === 'INCOME' ? '+' : '-'}{formatCurrency(tx.amount)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Sales Tab */}
        <TabsContent value="sales" className="space-y-6">
          {canEdit && (
            <div className="flex justify-end">
              <Button onClick={() => { setEditingSale(null); setShowSaleDialog(true) }}>
                <Plus className="mr-2 h-4 w-4" />
                Nova Venda
              </Button>
            </div>
          )}

          {buyer.sales?.length > 0 ? (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Unidade</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Area</TableHead>
                      <TableHead>Data da Venda</TableHead>
                      <TableHead className="text-right">Valor Venal</TableHead>
                      <TableHead className="text-right">Valor da Venda</TableHead>
                      <TableHead className="text-right">Permuta</TableHead>
                      <TableHead>Status</TableHead>
                      {canEdit && <TableHead className="w-20" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {buyer.sales.map((sale: any) => (
                      <TableRow key={sale.id}>
                        <TableCell className="font-medium">
                          {sale.unit?.code || '-'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {sale.unit?.type || '-'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {sale.unit?.area ? `${Number(sale.unit.area)} m2` : '-'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(sale.saleDate)}
                        </TableCell>
                        <TableCell className="text-right text-sm text-neutral-500">
                          {sale.unit?.price ? formatCurrency(Number(sale.unit.price)) : '-'}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          {formatCurrency(sale.salePrice)}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {sale.permutas?.length > 0 ? (
                            <span className="font-medium text-amber-600">
                              {formatCurrency(sale.permutas.reduce((s: number, p: any) => s + Number(p.value), 0))}
                            </span>
                          ) : (
                            <span className="text-neutral-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={SALE_STATUS_COLORS[sale.status] || ''}
                          >
                            {SALE_STATUS_LABELS[sale.status] || sale.status}
                          </Badge>
                        </TableCell>
                        {canEdit && (
                          <TableCell className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => {
                                setEditingSale(sale)
                                setShowSaleDialog(true)
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700"
                              disabled={deletingSaleId === sale.id}
                              onClick={() => {
                                if (confirm('Tem certeza que deseja excluir esta venda? A unidade voltará ao status disponível.')) {
                                  setDeletingSaleId(sale.id)
                                  deleteSaleMutation.mutate(sale.id)
                                }
                              }}
                            >
                              {deletingSaleId === sale.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-16">
              <ShoppingBag className="h-12 w-12 text-neutral-300" />
              <h3 className="mt-4 text-lg font-medium text-neutral-900">
                Nenhuma venda registrada
              </h3>
              <p className="mt-2 text-neutral-500">
                As vendas vinculadas a este comprador aparecerao aqui.
              </p>
              {canEdit && (
                <Button className="mt-6" onClick={() => { setEditingSale(null); setShowSaleDialog(true) }}>
                  <Plus className="mr-2 h-4 w-4" />
                  Registrar Primeira Venda
                </Button>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      {showEditDialog && (
        <BuyerFormDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          buyer={buyer}
        />
      )}

      {/* Sale Dialog */}
      {showSaleDialog && (
        <SaleFormDialog
          open={showSaleDialog}
          onOpenChange={(open) => {
            setShowSaleDialog(open)
            if (!open) setEditingSale(null)
          }}
          buyerId={id!}
          sale={editingSale}
        />
      )}

      {/* Member Dialog */}
      {showMemberDialog && (
        <BuyerMemberDialog
          open={showMemberDialog}
          onOpenChange={setShowMemberDialog}
          buyerId={id!}
          member={editingMember}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir Comprador</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o comprador{' '}
              <strong>{buyer.name}</strong>?
              {buyer._count?.sales > 0 && (
                <>
                  {' '}Este comprador possui {buyer._count.sales} venda(s) vinculada(s)
                  e sera desativado ao inves de excluido.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
